/**
 * Router.
 *
 * Hash-free history routing over a small route table. Each app route maps to one
 * of the prototype's 26 panels, so the URL is shareable — a relative can send
 * "look at Dada's page" as a link rather than "click the tree then find him".
 *
 * Screens are loaded lazily. A visitor who opens the marketing page never
 * downloads the tree renderer, and someone who only uses the calendar never
 * downloads the memorial screens.
 */

import { session } from './state/session.js';

/**
 * Route table.
 *
 * `key` matches the prototype's panel keys (MENU in linyay.html), so the
 * navigation rail and the router agree without a translation layer.
 *
 * `auth`   requires a signed-in user
 * `tree`   requires an active tree
 * `phase`  which build phase the screen lands in; unbuilt routes render a
 *          placeholder that names what is coming rather than a 404
 */
export const ROUTES = [
  // --- Public ------------------------------------------------------------- //
  { path: '/', key: 'landing', auth: false, tree: false, load: () => import('./marketing/landing.js') },
  { path: '/sign-in', key: 'signin', auth: false, tree: false, load: () => import('./screens/auth/sign-in.js') },
  { path: '/sign-up', key: 'signup', auth: false, tree: false, load: () => import('./screens/auth/sign-up.js') },
  { path: '/forgot', key: 'forgot', auth: false, tree: false, load: () => import('./screens/auth/forgot-password.js') },
  { path: '/reset/:token', key: 'reset', auth: false, tree: false, load: () => import('./screens/auth/reset-password.js') },
  // The invitation link a relative receives by text or email.
  { path: '/join/:token', key: 'join', auth: false, tree: false, load: () => import('./screens/auth/join.js') },
  // The address engraved on a brass plaque. Short, public, and it does not move — somebody may
  // be reading it off a headstone years after everything else here has been rearranged.
  { path: '/remember/:token', key: 'remember', auth: false, tree: false, load: () => import('./screens/remember.js') },

  // --- Every day ---------------------------------------------------------- //
  { path: '/app', key: 'today', auth: true, tree: true, phase: 1, load: () => import('./screens/today.js') },
  { path: '/app/on-this-day', key: 'onthis', auth: true, tree: true, phase: 1, load: () => import('./screens/on-this-day.js') },

  // --- The tree ----------------------------------------------------------- //
  { path: '/app/tree', key: 'tree', auth: true, tree: true, phase: 0, load: () => import('./screens/tree.js') },
  { path: '/app/people/:id', key: 'person', auth: true, tree: true, phase: 0, load: () => import('./screens/person.js') },
  { path: '/app/add', key: 'add', auth: true, tree: true, phase: 0, load: () => import('./screens/add.js') },
  { path: '/app/invite', key: 'invite', auth: true, tree: true, phase: 0, load: () => import('./screens/invite.js') },
  { path: '/app/album', key: 'album', auth: true, tree: true, phase: 1, load: () => import('./screens/album.js') },
  { path: '/app/photos-by-email', key: 'mailin', auth: true, tree: true, phase: 1, load: () => import('./screens/photos-by-email.js') },
  { path: '/app/video', key: 'video', auth: true, tree: true, phase: 3, load: () => import('./screens/video.js') },
  { path: '/app/memorials', key: 'memorial', auth: true, tree: true, phase: 3, load: () => import('./screens/memorials.js') },

  // --- Together ----------------------------------------------------------- //
  { path: '/app/blog', key: 'blog', auth: true, tree: true, phase: 1, load: () => import('./screens/blog.js') },
  { path: '/app/messages', key: 'messages', auth: true, tree: true, phase: 2, load: () => import('./screens/messages.js') },
  { path: '/app/recipes', key: 'recipes', auth: true, tree: true, phase: 2, load: () => import('./screens/recipes.js') },
  { path: '/app/map', key: 'map', auth: true, tree: true, phase: 4, load: () => import('./screens/map.js') },
  { path: '/app/calls', key: 'calls', auth: true, tree: true, phase: 4, load: () => import('./screens/calls.js') },

  // --- Care --------------------------------------------------------------- //
  { path: '/app/support', key: 'support', auth: true, tree: true, phase: 2, load: () => import('./screens/support.js') },
  { path: '/app/money', key: 'money', auth: true, tree: true, phase: 2 },
  { path: '/app/safe', key: 'safe', auth: true, tree: true, phase: 4, load: () => import('./screens/safe.js') },

  // --- Run the day -------------------------------------------------------- //
  { path: '/app/calendar', key: 'calendar', auth: true, tree: true, phase: 1, load: () => import('./screens/calendar.js') },
  { path: '/app/private-calendar', key: 'private', auth: true, tree: true, phase: 4, load: () => import('./screens/private-calendar.js') },
  { path: '/app/magic-import', key: 'magic', auth: true, tree: true, phase: 1, load: () => import('./screens/magic-import.js') },
  { path: '/app/meals', key: 'meals', auth: true, tree: true, phase: 4, load: () => import('./screens/meals.js') },
  { path: '/app/chores', key: 'chores', auth: true, tree: true, phase: 4, load: () => import('./screens/chores.js') },
  { path: '/app/events', key: 'events', auth: true, tree: true, phase: 1, load: () => import('./screens/events.js') },
  { path: '/app/reunion', key: 'reunion', auth: true, tree: true, phase: 4, load: () => import('./screens/reunion.js') },
  { path: '/app/cabinet', key: 'cabinet', auth: true, tree: true, phase: 3, load: () => import('./screens/cabinet.js') },
  { path: '/app/calendars', key: 'sync', auth: true, tree: true, phase: 1, load: () => import('./screens/calendars.js') },

  // --- Account (tree-agnostic) -------------------------------------------- //
  { path: '/app/account', key: 'account', auth: true, tree: false, phase: 0, load: () => import('./screens/account.js') },
  { path: '/app/trees', key: 'trees', auth: true, tree: false, phase: 0, load: () => import('./screens/trees.js') },
];

/** Compile '/app/people/:id' into a matcher. */
function compile(path) {
  const names = [];
  const pattern = path
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;
      names.push(segment.slice(1));
      return '([^/]+)';
    })
    .join('/');

  return { regex: new RegExp(`^${pattern}/?$`), names };
}

const COMPILED = ROUTES.map((route) => ({ ...route, ...compile(route.path) }));

/**
 * Reduce whatever was handed to the router down to a bare path it can match.
 *
 * Callers pass all sorts of things: `navigate()` is given full targets with a query
 * (`/sign-in?next=%2Fapp`), the delegated link handler passes an anchor's raw href
 * which may carry a fragment, and a browser may land on `/index.html`. The route
 * patterns match paths only, so anything else has to come off first — otherwise a
 * perfectly good route reports "that page is not here", which is how the sign-in
 * redirect and every marketing call to action broke.
 */
function normalise(target) {
  let path = String(target ?? '/');

  // Query and fragment are read separately by the screens; they are not part of
  // route matching.
  const cut = Math.min(
    ...[path.indexOf('?'), path.indexOf('#')].filter((index) => index !== -1),
    path.length,
  );
  path = path.slice(0, cut);

  path = path.replace(/\/{2,}/g, '/');
  if (path.endsWith('/index.html')) path = path.slice(0, -'index.html'.length);
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);

  return path || '/';
}

export function matchRoute(rawPathname) {
  const pathname = normalise(rawPathname);

  for (const route of COMPILED) {
    const match = route.regex.exec(pathname);
    if (!match) continue;

    const params = {};
    route.names.forEach((name, index) => {
      params[name] = decodeURIComponent(match[index + 1]);
    });

    return { route, params };
  }
  return null;
}

class Router extends EventTarget {
  constructor() {
    super();
    this.current = null;
    this._guard = null;
    this._onPopState = () => this.resolve(window.location.pathname);
  }

  start() {
    window.addEventListener('popstate', this._onPopState);

    // One delegated listener for every internal link, which is how the prototype
    // handles the rail, the tree, and the feed. Cheaper than one listener per
    // node when a tree can hold hundreds.
    document.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href^="/"]');
      if (!anchor || anchor.target === '_blank' || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      this.navigate(anchor.getAttribute('href'));
    });

    return this.resolve(window.location.pathname);
  }

  /**
   * Something on the screen would be lost by leaving, and says so.
   *
   * Registered by a screen that holds work the server has not got yet. A recording is the case
   * this was written for: somebody records their grandmother for twenty minutes, presses
   * "Family Tree" in the sidebar, and it is gone — silently, with no draft and no second
   * chance at the conversation. There is no undo for that anywhere in the product because
   * there is nothing left to undo from.
   *
   * A function rather than a flag, so the screen decides *when* it is dirty rather than
   * remembering to clear a boolean on every path out.
   */
  guard(ask) {
    this._guard = ask;
    return () => {
      if (this._guard === ask) this._guard = null;
    };
  }

  /** Whether leaving is allowed, asking the current screen if it registered a guard. */
  mayLeave() {
    const ask = this._guard;
    if (typeof ask !== 'function') return true;

    const reason = ask();
    if (!reason) return true;

    return window.confirm(reason);
  }

  navigate(pathname, { replace = false } = {}) {
    if (!this.mayLeave()) return undefined;

    if (replace) window.history.replaceState({}, '', pathname);
    else window.history.pushState({}, '', pathname);
    return this.resolve(pathname);
  }

  async resolve(pathname) {
    // Whatever the last screen was protecting, it is not on screen any more.
    this._guard = null;
    const matched = matchRoute(pathname);

    if (!matched) {
      this.dispatchEvent(new CustomEvent('not-found', { detail: { pathname } }));
      return;
    }

    const { route, params } = matched;

    // Guards. Redirect rather than error: a relative following a link from a
    // text message should land on sign-in, not on a stack trace.
    if (route.auth && !session.isAuthenticated) {
      const next = encodeURIComponent(pathname);
      return this.navigate(`/sign-in?next=${next}`, { replace: true });
    }

    if (route.tree && !session.activeTreeId) {
      // Signed in but not in any tree — a brand-new account, or someone just
      // removed from the only tree they were on.
      return this.navigate('/app/trees', { replace: true });
    }

    this.current = { route, params, pathname };

    // Unbuilt screens render a named placeholder rather than a dead end, so it is
    // always obvious whether something is missing or broken.
    if (!route.load) {
      this.dispatchEvent(
        new CustomEvent('render-placeholder', { detail: { route, params } }),
      );
      return;
    }

    // Every screen paints its own loading state, but only once its module has arrived — and
    // fetching that module is itself a request. Until it landed, the screen the reader had
    // just left stayed on display: clicking "Account" from Video Messages left Video Messages
    // up, so the press appeared to have done nothing. Measured at 300ms on a local machine,
    // which is the best case.
    //
    // Announced rather than rendered here, because the router does not know about the shell or
    // which surface this route belongs to. It fires only for a route that has a module to
    // fetch, and `render` follows it immediately when that module is already cached.
    this.dispatchEvent(new CustomEvent('render-loading', { detail: { route, params } }));

    const module = await route.load();
    this.dispatchEvent(
      new CustomEvent('render', { detail: { route, params, module } }),
    );
  }
}

export const router = new Router();
